import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  Select,
  MenuItem,
  Paper,
  Button,
  Stack,
  TextField,
  Chip,
  IconButton,
  CircularProgress,
  Snackbar,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Tooltip,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SendIcon from "@mui/icons-material/Send";
import GroupsIcon from "@mui/icons-material/Groups";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import LinkIcon from "@mui/icons-material/Link";
import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import FormatAlignCenterIcon from "@mui/icons-material/FormatAlignCenter";
import FormatAlignRightIcon from "@mui/icons-material/FormatAlignRight";
import FormatClearIcon from "@mui/icons-material/FormatClear";
import FormatStrikethroughIcon from "@mui/icons-material/FormatStrikethrough";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import CodeIcon from "@mui/icons-material/Code";
import ImageIcon from "@mui/icons-material/Image";

import Navbar from "./AdminNav";
import Footer from "../Footer";
import api from "../api/api";
import { ENDPOINTS } from "../api/endpoints"; // adjust path to match where ENDPOINTS actually lives

/* =========================================================
   Minimal inline rich-text editor (no external dependency).
   If your project already has react-quill / TinyMCE installed,
   swap this out - just keep the same `initialValue` +
   `onChange(html)` contract and the rest of the page keeps working.
========================================================= */
const RichTextEditor = ({
  initialValue,
  onChange,
  minHeight = 280,
}) => {
  const editorRef = useRef(null);
  const htmlValueRef = useRef(initialValue || "");
  const savedSelectionRef = useRef(null);
  const imageInputRef = useRef(null);

  const [mode, setMode] = useState("visual");
  const [htmlValue, setHtmlValue] = useState(initialValue || "");

  /* ---------------------------------------------------------
     INITIAL VALUE
  --------------------------------------------------------- */

  useEffect(() => {
    const value = initialValue || "";
    htmlValueRef.current = value;
    setHtmlValue(value);

    if (editorRef.current) {
      editorRef.current.innerHTML = value;
    }
  }, []);

  /* ---------------------------------------------------------
     SELECTION
  --------------------------------------------------------- */

  const saveSelection = () => {
    const selection = window.getSelection();

    if (!selection || selection.rangeCount === 0) {
      return;
    }

    const range = selection.getRangeAt(0);

    if (
      editorRef.current &&
      editorRef.current.contains(range.commonAncestorContainer)
    ) {
      savedSelectionRef.current = range.cloneRange();
    }
  };

  const restoreSelection = () => {
    const selection = window.getSelection();
    const savedRange = savedSelectionRef.current;

    if (!selection || !savedRange) {
      return false;
    }

    try {
      selection.removeAllRanges();
      selection.addRange(savedRange);
      return true;
    } catch {
      return false;
    }
  };

  /* ---------------------------------------------------------
     CHANGE
  --------------------------------------------------------- */

  const emitChange = () => {
    if (!editorRef.current) {
      return;
    }

    const html = editorRef.current.innerHTML;

    setHtmlValue(html);
    onChange(html);
  };

  /* ---------------------------------------------------------
     FORMATTING
  --------------------------------------------------------- */

  const exec = (command, value = null) => {
    restoreSelection();

    document.execCommand(command, false, value);

    emitChange();
    saveSelection();
  };

  const handleToolbarMouseDown = (event, command, value = null) => {
    /*
     * VERY IMPORTANT:
     *
     * Preventing the default mousedown keeps the current
     * text selection inside the contentEditable editor.
     *
     * This is especially important for Italic/Bold/etc.
     */
    event.preventDefault();

    saveSelection();

    exec(command, value);
  };

  /* ---------------------------------------------------------
     BLOCK / HEADINGS
  --------------------------------------------------------- */

  const handleBlockChange = (event) => {
    const value = event.target.value;

    saveSelection();

    if (!restoreSelection()) {
      return;
    }

    document.execCommand("formatBlock", false, value);

    emitChange();
    saveSelection();
  };

  /* ---------------------------------------------------------
     ALIGNMENT
  --------------------------------------------------------- */

  const handleAlignment = (event, alignment) => {
    event.preventDefault();

    saveSelection();

    exec(`justify${alignment}`);
  };

  const handleImageUpload = async (event) => {
  const file = event.target.files?.[0];

  if (!file) {
    return;
  }

  // Only allow images
  if (!file.type.startsWith("image/")) {
    alert("Please select an image file.");
    event.target.value = "";
    return;
  }

  try {
    const formData = new FormData();

    // Swagger expects the field name "files"
    formData.append("files", file);

    const response = await api.post(
      "/api/NewCastingNotice/UploadFiles",
      formData
    );

    const imageUrl = response.data?.data?.[0]?.url;

    if (!imageUrl) {
      throw new Error("Image URL was not returned by the server.");
    }

    // Put the cursor back where the user was typing
    restoreSelection();

    // Insert uploaded image into the editor
    document.execCommand(
      "insertImage",
      false,
      imageUrl
    );

    emitChange();
    saveSelection();

  } catch (error) {
    console.error("Image upload failed:", error);

    alert("Failed to upload image. Please try again.");
  } finally {
    // Allows selecting the same image again
    event.target.value = "";
  }
};

  /* ---------------------------------------------------------
     LINK
  --------------------------------------------------------- */

  const handleLink = (event) => {
    event.preventDefault();

    saveSelection();

    const url = window.prompt("Enter URL:");

    if (!url) {
      restoreSelection();
      return;
    }

    restoreSelection();

    document.execCommand("createLink", false, url);

    emitChange();
    saveSelection();
  };

  /* ---------------------------------------------------------
     CLEAR FORMATTING
  --------------------------------------------------------- */

  const handleClearFormatting = (event) => {
    event.preventDefault();

    saveSelection();

    exec("removeFormat");
  };

  /* ---------------------------------------------------------
     HTML MODE
  --------------------------------------------------------- */

  const switchToHtml = () => {
    const currentHtml = editorRef.current?.innerHTML || "";

    htmlValueRef.current = currentHtml;
    setHtmlValue(currentHtml);

    setMode("html");
  };

  const switchToVisual = () => {
    const html = htmlValueRef.current || "";

    setHtmlValue(html);
    onChange(html);
    setMode("visual");

    requestAnimationFrame(() => {
      if (!editorRef.current) return;

      // Visual editor now exists, so insert the HTML here
      editorRef.current.innerHTML = html;

      editorRef.current.focus();

      const selection = window.getSelection();
      const range = document.createRange();

      range.selectNodeContents(editorRef.current);
      range.collapse(false);

      selection.removeAllRanges();
      selection.addRange(range);

      savedSelectionRef.current = range.cloneRange();
    });
  };
  const handleHtmlChange = (event) => {
    const value = event.target.value;

    htmlValueRef.current = value;
    setHtmlValue(value);
    onChange(value);
  };

  /* ---------------------------------------------------------
     INPUT
  --------------------------------------------------------- */

  const handleInput = (event) => {
    const html = event.currentTarget.innerHTML;
    htmlValueRef.current = html;
    setHtmlValue(html);
    onChange(html);

    saveSelection();
  };
  /* ---------------------------------------------------------
     VISUAL EDITOR
  --------------------------------------------------------- */

  const renderVisualEditor = () => (
    <>
      {/* Toolbar */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 0.5,
          p: 1,
          borderBottom: "1px solid #e0e0e0",
          backgroundColor: "#fafafa",
        }}
      >
        {/* Heading selector */}
        <select
          defaultValue="p"
          onMouseDown={() => saveSelection()}
          onChange={handleBlockChange}
          title="Text style"
          style={{
            height: "32px",
            border: "1px solid #d0d5dd",
            borderRadius: "4px",
            backgroundColor: "#fff",
            padding: "0 8px",
            fontSize: "13px",
            cursor: "pointer",
          }}
        >
          <option value="p">Normal</option>
          <option value="h1">Heading 1</option>
          <option value="h2">Heading 2</option>
          <option value="h3">Heading 3</option>
        </select>

        <Box
          sx={{
            width: "1px",
            height: "24px",
            backgroundColor: "#ddd",
            mx: 0.5,
          }}
        />

        {/* Bold */}
        <Tooltip title="Bold">
          <IconButton
            size="small"
            onMouseDown={(e) =>
              handleToolbarMouseDown(e, "bold")
            }
          >
            <FormatBoldIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        {/* Italic */}
        <Tooltip title="Italic">
          <IconButton
            size="small"
            onMouseDown={(e) =>
              handleToolbarMouseDown(e, "italic")
            }
          >
            <FormatItalicIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        {/* Underline */}
        <Tooltip title="Underline">
          <IconButton
            size="small"
            onMouseDown={(e) =>
              handleToolbarMouseDown(e, "underline")
            }
          >
            <FormatUnderlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        {/* Strikethrough */}
        <Tooltip title="Strikethrough">
          <IconButton
            size="small"
            onMouseDown={(e) =>
              handleToolbarMouseDown(e, "strikeThrough")
            }
          >
            <FormatStrikethroughIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Box
          sx={{
            width: "1px",
            height: "24px",
            backgroundColor: "#ddd",
            mx: 0.5,
          }}
        />

        {/* Bullet list */}
        <Tooltip title="Bullet list">
          <IconButton
            size="small"
            onMouseDown={(e) =>
              handleToolbarMouseDown(
                e,
                "insertUnorderedList"
              )
            }
          >
            <FormatListBulletedIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        {/* Numbered list */}
        <Tooltip title="Numbered list">
          <IconButton
            size="small"
            onMouseDown={(e) =>
              handleToolbarMouseDown(
                e,
                "insertOrderedList"
              )
            }
          >
            <FormatListNumberedIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Box
          sx={{
            width: "1px",
            height: "24px",
            backgroundColor: "#ddd",
            mx: 0.5,
          }}
        />

        {/* Alignment */}
        <Tooltip title="Align left">
          <IconButton
            size="small"
            onMouseDown={(e) =>
              handleAlignment(e, "Left")
            }
          >
            <FormatAlignLeftIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title="Align center">
          <IconButton
            size="small"
            onMouseDown={(e) =>
              handleAlignment(e, "Center")
            }
          >
            <FormatAlignCenterIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title="Align right">
          <IconButton
            size="small"
            onMouseDown={(e) =>
              handleAlignment(e, "Right")
            }
          >
            <FormatAlignRightIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Box
          sx={{
            width: "1px",
            height: "24px",
            backgroundColor: "#ddd",
            mx: 0.5,
          }}
        />

        {/* Link */}
        <Tooltip title="Insert link">
          <IconButton
            size="small"
            onMouseDown={handleLink}
          >
            <LinkIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        {/* Image */}
<Tooltip title="Insert image">
  <IconButton
    size="small"
    onMouseDown={(e) => {
      e.preventDefault();

      saveSelection();

      imageInputRef.current?.click();
    }}
  >
    <ImageIcon fontSize="small" />
  </IconButton>
</Tooltip>

<input
  ref={imageInputRef}
  type="file"
  accept="image/*"
  style={{ display: "none" }}
  onChange={handleImageUpload}
/>

        {/* Quote */}
        <Tooltip title="Quote">
          <IconButton
            size="small"
            onMouseDown={(e) =>
              handleToolbarMouseDown(
                e,
                "formatBlock",
                "blockquote"
              )
            }
          >
            <FormatQuoteIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        {/* Clear formatting */}
        <Tooltip title="Clear formatting">
          <IconButton
            size="small"
            onMouseDown={handleClearFormatting}
          >
            <FormatClearIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Box sx={{ flexGrow: 1 }} />

        {/* HTML mode */}
        <Tooltip title="Edit HTML">
          <Button
            size="small"
            variant="outlined"
            startIcon={<CodeIcon fontSize="small" />}
            onClick={switchToHtml}
          >
            HTML
          </Button>
        </Tooltip>
      </Box>

      {/* Visual editor */}
      <Box
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        onMouseUp={saveSelection}
        onKeyUp={saveSelection}
        onBlur={saveSelection}
sx={{
  minHeight,
  p: 2,
  outline: "none",
  fontSize: 14,
  lineHeight: 1.6,
  overflowY: "auto",

  "&:focus": {
    outline: "none",
  },

  "& p": {
    margin: "0 0 12px 0",
  },

  "& h1": {
    fontSize: "28px",
    lineHeight: 1.2,
    fontWeight: 700,
    margin: "16px 0 12px",
  },

  "& h2": {
    fontSize: "22px",
    lineHeight: 1.3,
    fontWeight: 700,
    margin: "14px 0 10px",
  },

  "& h3": {
    fontSize: "18px",
    lineHeight: 1.4,
    fontWeight: 600,
    margin: "12px 0 8px",
  },

  /* ---------------- LISTS ---------------- */

  "& ul": {
    listStyleType: "disc !important",
    display: "block",
    paddingLeft: "28px",
    margin: "8px 0 12px",
  },

  "& ol": {
    listStyleType: "decimal !important",
    display: "block",
    paddingLeft: "28px",
    margin: "8px 0 12px",
  },

  "& ul li": {
    display: "list-item !important",
    listStyleType: "disc !important",
    marginBottom: "4px",
  },

  "& ol li": {
    display: "list-item !important",
    listStyleType: "decimal !important",
    marginBottom: "4px",
  },

  /* Nested lists */

  "& ul ul": {
    listStyleType: "circle !important",
  },

  "& ul ul li": {
    listStyleType: "circle !important",
  },

  "& ol ol": {
    listStyleType: "lower-alpha !important",
  },

  "& ol ol li": {
    listStyleType: "lower-alpha !important",
  },

  /* ---------------- OTHER ELEMENTS ---------------- */

  "& a": {
    color: "#1976d2",
    textDecoration: "underline",
  },

  "& img": {
  maxWidth: "100%",
  height: "auto",
  display: "block",
  margin: "12px 0",
},
  "& blockquote": {
    borderLeft: "4px solid #ccc",
    margin: "12px 0",
    paddingLeft: "16px",
    color: "#666",
  },

}}
      />
    </>
  );

  /* ---------------------------------------------------------
     HTML EDITOR
  --------------------------------------------------------- */

  const renderHtmlEditor = () => (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 1.5,
          py: 1,
          borderBottom: "1px solid #e0e0e0",
          backgroundColor: "#fafafa",
        }}
      >
        <Typography
          variant="caption"
          sx={{
            fontWeight: 600,
            color: "text.secondary",
          }}
        >
          HTML SOURCE
        </Typography>

        <Button
          size="small"
          variant="contained"
          onClick={switchToVisual}
        >
          Visual Editor
        </Button>
      </Box>

      <TextField
        value={htmlValue}
        onChange={handleHtmlChange}
        multiline
        fullWidth
        variant="outlined"
        placeholder={`<h1>Heading</h1>
<p>Hello <strong>actors</strong>,</p>

<h2>Project Details</h2>

<ul>
  <li>Location: New York</li>
  <li>Pay: Paid</li>
</ul>`}
        InputProps={{
          sx: {
            minHeight,
            alignItems: "flex-start",
            fontFamily:
              '"SFMono-Regular", Consolas, "Liberation Mono", monospace',
            fontSize: "13px",
            lineHeight: 1.6,
            backgroundColor: "#fff",
          },
        }}
        sx={{
          "& .MuiOutlinedInput-notchedOutline": {
            border: "none",
          },
        }}
      />

      <Box
        sx={{
          px: 2,
          py: 1,
          borderTop: "1px solid #e0e0e0",
          backgroundColor: "#fafafa",
        }}
      >
        <Typography
          variant="caption"
          color="text.secondary"
        >
          Supported tags: h1, h2, h3, p, strong, em, u, s,
          ul, ol, li, a, blockquote, br, hr, img
        </Typography>
      </Box>
    </>
  );

  return (
    <Paper variant="outlined">
      {mode === "visual"
        ? renderVisualEditor()
        : renderHtmlEditor()}
    </Paper>
  );
};

/* =========================================================
   MAIN PAGE COMPONENT
========================================================= */
const Broadcast = () => {
  const clientId = localStorage.getItem("userId") || 0;

  /* ---------- Audience ---------- */
  const [audienceTypes, setAudienceTypes] = useState([]);
  const [selectedAudienceType, setSelectedAudienceType] = useState("");
  const [audienceCount, setAudienceCount] = useState(null);
  const [audienceCountLoading, setAudienceCountLoading] = useState(false);

  /* ---------- Events ---------- */
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState("");
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventDialog, setEventDialog] = useState({
    open: false,
    mode: "add", // "add" | "edit"
    eventId: 0,
    eventName: "",
    eventDescription: "",
  });
  const [eventDeleteConfirm, setEventDeleteConfirm] = useState({ open: false, event: null });
  const [eventSaving, setEventSaving] = useState(false);

  /* ---------- Templates (EventMessages) ---------- */
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templateDialog, setTemplateDialog] = useState({
    open: false,
    mode: "add",
    eventMessageId: 0,
    subject: "",
    messageBody: "",
  });
  const [templateDeleteConfirm, setTemplateDeleteConfirm] = useState({ open: false, template: null });
  const [templateSaving, setTemplateSaving] = useState(false);

  /* ---------- Working copy that actually gets sent ---------- */
  const [subject, setSubject] = useState("");
  const [messageBody, setMessageBody] = useState("");
  const [editorKey, setEditorKey] = useState(0); // bump to force RichTextEditor remount

  /* ---------- Send ---------- */
  const [sendConfirmOpen, setSendConfirmOpen] = useState(false);
  const [sending, setSending] = useState(false);

  /* ---------- Email Preview ---------- */
  const [emailPreviewOpen, setEmailPreviewOpen] = useState(false);

  /* ---------- Toast ---------- */
  const [toast, setToast] = useState({ open: false, message: "", severity: "info" });
  const showToast = (message, severity = "success") => setToast({ open: true, message, severity });
  const handleCloseToast = () => setToast((prev) => ({ ...prev, open: false }));

  /* =========================================================
     FETCHERS
  ========================================================= */
  const fetchAudienceTypes = useCallback(async () => {
    try {
      const res = await api.get(ENDPOINTS.getAudienceTypes());
      setAudienceTypes(res.data?.data || []);
    } catch (err) {
      showToast("Failed to load audience types", "error");
    }
  }, []);

  const fetchAudienceCount = useCallback(async (audienceTypeId) => {
    if (!audienceTypeId) {
      setAudienceCount(null);
      return;
    }
    setAudienceCountLoading(true);
    try {
      const res = await api.get(ENDPOINTS.getAudienceCount({ audienceTypeId }));
      setAudienceCount(res.data?.count);
    } catch (err) {
      setAudienceCount(null);
      showToast("Failed to load recipient count", "error");
    } finally {
      setAudienceCountLoading(false);
    }
  }, []);

  const fetchEvents = useCallback(async () => {
    setEventsLoading(true);
    try {
      const res = await api.get(ENDPOINTS.getEvents());
      setEvents(res.data?.data || []);
    } catch (err) {
      showToast("Failed to load events", "error");
    } finally {
      setEventsLoading(false);
    }
  }, []);

  const fetchTemplates = useCallback(async (eventId, audienceTypeId) => {
    if (!eventId || !audienceTypeId) {
      setTemplates([]);
      return;
    }
    setTemplatesLoading(true);
    try {
      const res = await api.get(ENDPOINTS.getEventMessages({ eventId, audienceTypeId }));
      setTemplates(res.data?.data || []);
    } catch (err) {
      showToast("Failed to load message templates", "error");
    } finally {
      setTemplatesLoading(false);
    }
  }, []);

  /* Initial load */
  useEffect(() => {
    fetchAudienceTypes();
    fetchEvents();
  }, [fetchAudienceTypes, fetchEvents]);

  /* Audience type change */
  const handleAudienceTypeChange = (e) => {
    const id = e.target.value;
    setSelectedAudienceType(id);
    setSelectedTemplateId("");
    setSubject("");
    setMessageBody("");
    fetchAudienceCount(id);
    if (selectedEvent) fetchTemplates(selectedEvent, id);
  };

  /* Event change */
  const handleEventChange = (e) => {
    const id = e.target.value;
    setSelectedEvent(id);
    setSelectedTemplateId("");
    setSubject("");
    setMessageBody("");
    if (selectedAudienceType) fetchTemplates(id, selectedAudienceType);
  };

  /* Template change -> load subject/body into the working copy */
  const handleTemplateChange = (e) => {
    const id = e.target.value;
    setSelectedTemplateId(id);
    const tpl = templates.find((t) => t.eventMessageId === id);
    setSubject(tpl?.subject || "");
    setMessageBody(tpl?.messageBody || "");
    setEditorKey((k) => k + 1); // force editor to reset to the new template's body
  };

  /* =========================================================
     EVENT CRUD
  ========================================================= */
  const openAddEvent = () =>
    setEventDialog({ open: true, mode: "add", eventId: 0, eventName: "", eventDescription: "" });

  const openEditEvent = () => {
    const ev = events.find((e) => e.eventId === selectedEvent);
    if (!ev) return;
    setEventDialog({
      open: true,
      mode: "edit",
      eventId: ev.eventId,
      eventName: ev.eventName,
      eventDescription: ev.eventDescription,
    });
  };

  const closeEventDialog = () => setEventDialog((prev) => ({ ...prev, open: false }));

  const handleSaveEvent = async () => {
    if (!eventDialog.eventName.trim()) {
      showToast("Event name is required", "warning");
      return;
    }
    setEventSaving(true);
    const payload = {
      eventId: eventDialog.eventId,
      eventName: eventDialog.eventName,
      eventDescription: eventDialog.eventDescription,
      userId: Number(clientId) || 0,
    };
    try {
      const res =
        eventDialog.mode === "add"
          ? await api.post(ENDPOINTS.addEvent(), payload)
          : await api.put(ENDPOINTS.updateEvent(), payload);

      const success = res.data?.status?.toLowerCase() === "success" || res.data?.result === true;

      if (success) {
        showToast(eventDialog.mode === "add" ? "Event created" : "Event updated");
        closeEventDialog();
        await fetchEvents();
      } else {
        showToast("Failed to save event", "error");
      }
    } catch (err) {
      showToast("Error saving event", "error");
    } finally {
      setEventSaving(false);
    }
  };

  const handleDeleteEvent = async () => {
    const ev = eventDeleteConfirm.event;
    if (!ev) return;
    try {
      const res = await api.delete(ENDPOINTS.deleteEvent({ eventId: ev.eventId }));
      const success = res.data?.status?.toLowerCase() === "success" || res.data?.result === true;

      if (success) {
        showToast("Event deleted");
        if (selectedEvent === ev.eventId) {
          setSelectedEvent("");
          setTemplates([]);
          setSelectedTemplateId("");
          setSubject("");
          setMessageBody("");
        }
        await fetchEvents();
      } else {
        showToast("Failed to delete event", "error");
      }
    } catch (err) {
      showToast("Error deleting event", "error");
    } finally {
      setEventDeleteConfirm({ open: false, event: null });
    }
  };

  /* =========================================================
     TEMPLATE (EventMessage) CRUD
  ========================================================= */
  const openAddTemplate = () =>
    setTemplateDialog({ open: true, mode: "add", eventMessageId: 0, subject: "", messageBody: "" });

  const openEditTemplate = () => {
    const tpl = templates.find((t) => t.eventMessageId === selectedTemplateId);
    if (!tpl) return;
    setTemplateDialog({
      open: true,
      mode: "edit",
      eventMessageId: tpl.eventMessageId,
      subject: tpl.subject,
      messageBody: tpl.messageBody,
    });
  };

  const closeTemplateDialog = () => setTemplateDialog((prev) => ({ ...prev, open: false }));

  const saveTemplate = async ({
    eventMessageId,
    subject: templateSubject,
    messageBody: templateMessageBody,
  }) => {
    if (!templateSubject.trim()) {
      showToast("Template subject is required", "warning");
      return false;
    }

    setTemplateSaving(true);

    const payload = {
      eventMessageId,
      eventId: selectedEvent,
      audienceTypeId: selectedAudienceType,
      subject: templateSubject,
      messageBody: templateMessageBody,
      userId: Number(clientId) || 0,
    };

    try {
      const res =
        eventMessageId === 0
          ? await api.post(ENDPOINTS.addEventMessage(), payload)
          : await api.put(ENDPOINTS.updateEventMessage(), payload);

      const success =
        res.data?.status?.toLowerCase() === "success" ||
        res.data?.result === true;

      if (success) {
        showToast(
          eventMessageId === 0
            ? "Template created"
            : "Template saved successfully"
        );

        await fetchTemplates(selectedEvent, selectedAudienceType);

        return true;
      }

      showToast(
        eventMessageId === 0
          ? "Failed to create template"
          : "Failed to save template",
        "error"
      );

      return false;
    } catch (err) {
      showToast(
        eventMessageId === 0
          ? "Error creating template"
          : "Error saving template",
        "error"
      );

      return false;
    } finally {
      setTemplateSaving(false);
    }
  };

  const handleSaveCurrentTemplate = async () => {
    if (!selectedTemplateId) {
      showToast("Please select a template", "warning");
      return;
    }

    const saved = await saveTemplate({
      eventMessageId: selectedTemplateId,
      subject,
      messageBody,
    });

    if (saved) {
      // Keep the working editor in sync with the saved template
      setEditorKey((k) => k + 1);
    }
  };

  const handleSaveTemplate = async () => {
    const saved = await saveTemplate({
      eventMessageId: templateDialog.eventMessageId,
      subject: templateDialog.subject,
      messageBody: templateDialog.messageBody,
    });

    if (saved) {
      closeTemplateDialog();
    }
  };

  const handleDeleteTemplate = async () => {
    const tpl = templateDeleteConfirm.template;
    if (!tpl) return;
    try {
      const res = await api.delete(ENDPOINTS.deleteEventMessage({ eventMessageId: tpl.eventMessageId }));
      const success = res.data?.status?.toLowerCase() === "success" || res.data?.result === true;

      if (success) {
        showToast("Template deleted");
        if (selectedTemplateId === tpl.eventMessageId) {
          setSelectedTemplateId("");
          setSubject("");
          setMessageBody("");
        }
        await fetchTemplates(selectedEvent, selectedAudienceType);
      } else {
        showToast("Failed to delete template", "error");
      }
    } catch (err) {
      showToast("Error deleting template", "error");
    } finally {
      setTemplateDeleteConfirm({ open: false, template: null });
    }
  };

  /* =========================================================
     SEND MAIL
  ========================================================= */
  const canSend = Boolean(selectedAudienceType && selectedEvent && selectedTemplateId && subject.trim());

  const handleSendMail = async () => {
    setSending(true);
    try {
      const res = await api.post(ENDPOINTS.sendEventMail(), {
        audienceTypeId: selectedAudienceType,
        eventMessageId: selectedTemplateId,
        subject,
        messageBody,
      });

      const success = res.data?.status?.toLowerCase() === "success" || res.data?.result === true;

      if (success) {
        showToast(`Email sent to ${audienceCount ?? "all"} recipient(s)`);
      } else {
        showToast("Failed to send email", "error");
      }
    } catch (err) {
      showToast("Error sending email", "error");
    } finally {
      setSending(false);
      setSendConfirmOpen(false);
    }
  };

  const selectedAudienceName =
    audienceTypes.find((a) => a.audienceTypeId === selectedAudienceType)?.audienceName || "";
  const selectedEventName = events.find((e) => e.eventId === selectedEvent)?.eventName || "";

  /* =========================================================
     RENDER
  ========================================================= */
  return (
    <>
      <Navbar />

      <Box sx={{ maxWidth: "1000px", mx: "auto", p: 3 }}>
        <Typography variant="h4" mb={3}>
          Broadcast
        </Typography>

        {/* ---------------- STEP 1: AUDIENCE TYPE ---------------- */}
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" mb={2}>
            1. Choose Audience
          </Typography>

          <Stack direction="row" spacing={2} alignItems="center">
            <Select
              value={selectedAudienceType}
              onChange={handleAudienceTypeChange}
              displayEmpty
              sx={{ minWidth: 280 }}
              renderValue={(selected) => {
                if (!selected) return <span style={{ color: "#888" }}>-- Select Audience Type --</span>;
                return audienceTypes.find((a) => a.audienceTypeId === selected)?.audienceName || "";
              }}
            >
              {audienceTypes.map((a) => (
                <MenuItem key={a.audienceTypeId} value={a.audienceTypeId}>
                  {a.audienceName}
                </MenuItem>
              ))}
            </Select>

            {selectedAudienceType && (
              <Chip
                icon={<GroupsIcon />}
                label={
                  audienceCountLoading ? "Loading..." : `${audienceCount ?? 0} recipient(s)`
                }
                color="primary"
                variant="outlined"
              />
            )}
          </Stack>
        </Paper>

        {/* ---------------- STEP 2: EVENT ---------------- */}
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" mb={2}>
            2. Choose Event
          </Typography>

          <Stack direction="row" spacing={1} alignItems="center">
            <Select
              value={selectedEvent}
              onChange={handleEventChange}
              displayEmpty
              sx={{ minWidth: 280 }}
              disabled={eventsLoading}
              renderValue={(selected) => {
                if (!selected) return <span style={{ color: "#888" }}>-- Select Event --</span>;
                return events.find((e) => e.eventId === selected)?.eventName || "";
              }}
            >
              {events.map((ev) => (
                <MenuItem key={ev.eventId} value={ev.eventId}>
                  {ev.eventName}
                </MenuItem>
              ))}
            </Select>

            {eventsLoading && <CircularProgress size={20} />}

            <Tooltip title="Add event">
              <IconButton color="primary" onClick={openAddEvent}>
                <AddIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit selected event">
              <span>
                <IconButton disabled={!selectedEvent} onClick={openEditEvent}>
                  <EditIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Delete selected event">
              <span>
                <IconButton
                  disabled={!selectedEvent}
                  color="error"
                  onClick={() =>
                    setEventDeleteConfirm({
                      open: true,
                      event: events.find((e) => e.eventId === selectedEvent),
                    })
                  }
                >
                  <DeleteIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Paper>

        {/* ---------------- STEP 3: TEMPLATE ---------------- */}
        {selectedAudienceType && selectedEvent && (
          <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" mb={2}>
              3. Choose Message Template
            </Typography>

            <Stack direction="row" spacing={1} alignItems="center" mb={3}>
              <Select
                value={selectedTemplateId}
                onChange={handleTemplateChange}
                displayEmpty
                sx={{ minWidth: 320 }}
                disabled={templatesLoading}
                renderValue={(selected) => {
                  if (!selected) return <span style={{ color: "#888" }}>-- Select Template --</span>;
                  return templates.find((t) => t.eventMessageId === selected)?.subject || "";
                }}
              >
                {templates.map((t) => (
                  <MenuItem key={t.eventMessageId} value={t.eventMessageId}>
                    {t.subject}
                  </MenuItem>
                ))}
              </Select>

              {templatesLoading && <CircularProgress size={20} />}

              <Tooltip title="Add template">
                <IconButton color="primary" onClick={openAddTemplate}>
                  <AddIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Edit selected template">
                <span>
                  <IconButton disabled={!selectedTemplateId} onClick={openEditTemplate}>
                    <EditIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Delete selected template">
                <span>
                  <IconButton
                    disabled={!selectedTemplateId}
                    color="error"
                    onClick={() =>
                      setTemplateDeleteConfirm({
                        open: true,
                        template: templates.find((t) => t.eventMessageId === selectedTemplateId),
                      })
                    }
                  >
                    <DeleteIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>

            {templates.length === 0 && !templatesLoading && (
              <Typography color="text.secondary" mb={2}>
                No templates yet for this event + audience. Click + to create one.
              </Typography>
            )}

            {selectedTemplateId && (
              <>
                <Divider sx={{ mb: 3 }} />

                <Typography variant="subtitle2" mb={1}>
                  Subject
                </Typography>
                <TextField
                  fullWidth
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  sx={{ mb: 3 }}
                />

                <Typography variant="subtitle2" mb={1}>
                  Message Body
                </Typography>
                <RichTextEditor
                  key={editorKey}
                  initialValue={messageBody}
                  onChange={setMessageBody}
                />
              </>
            )}
          </Paper>
        )}

        {/* ---------------- STEP 4: ACTIONS ---------------- */}
        {selectedTemplateId && (
          <Box
            display="flex"
            justifyContent="flex-end"
            gap={2}
            mb={4}
          >
            {/* Save */}
            <Button
              variant="outlined"
              color="primary"
              size="large"
              onClick={handleSaveCurrentTemplate}
              disabled={templateSaving || !subject.trim()}
            >
              {templateSaving ? (
                <CircularProgress size={18} />
              ) : (
                "Save"
              )}
            </Button>

            {/* Preview Email */}
            <Button
              variant="outlined"
              color="primary"
              size="large"
              onClick={() => setEmailPreviewOpen(true)}
              disabled={!subject.trim() && !messageBody.trim()}
            >
              Preview Email
            </Button>

            {/* Send Mail */}
            <Button
              variant="contained"
              color="primary"
              size="large"
              startIcon={
                sending ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <SendIcon />
                )
              }
              disabled={!canSend || sending}
              onClick={() => setSendConfirmOpen(true)}
            >
              {sending ? "Sending..." : "Send Mail"}
            </Button>
          </Box>
        )}
      </Box>

      {/* ---------------- TOAST ---------------- */}
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={toast.severity}>{toast.message}</Alert>
      </Snackbar>

      {/* ---------------- EVENT ADD/EDIT DIALOG ---------------- */}
      <Dialog open={eventDialog.open} onClose={closeEventDialog} fullWidth maxWidth="sm">
        <DialogTitle>{eventDialog.mode === "add" ? "Add Event" : "Edit Event"}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Event Name"
            fullWidth
            value={eventDialog.eventName}
            onChange={(e) => setEventDialog((prev) => ({ ...prev, eventName: e.target.value }))}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Event Description"
            fullWidth
            multiline
            minRows={3}
            value={eventDialog.eventDescription}
            onChange={(e) => setEventDialog((prev) => ({ ...prev, eventDescription: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEventDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveEvent} disabled={eventSaving}>
            {eventSaving ? <CircularProgress size={18} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---------------- EVENT DELETE CONFIRM ---------------- */}
      <Dialog
        open={eventDeleteConfirm.open}
        onClose={() => setEventDeleteConfirm({ open: false, event: null })}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete the event "{eventDeleteConfirm.event?.eventName}"? This will also affect its message
            templates.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEventDeleteConfirm({ open: false, event: null })}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeleteEvent}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---------------- TEMPLATE ADD/EDIT DIALOG ---------------- */}
      <Dialog open={templateDialog.open} onClose={closeTemplateDialog} fullWidth maxWidth="md">
        <DialogTitle>{templateDialog.mode === "add" ? "Add Template" : "Edit Template"}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Event: {selectedEventName} &nbsp;|&nbsp; Audience: {selectedAudienceName}
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Subject"
            fullWidth
            value={templateDialog.subject}
            onChange={(e) => setTemplateDialog((prev) => ({ ...prev, subject: e.target.value }))}
            sx={{ mb: 2 }}
          />
          <Typography variant="subtitle2" mb={1}>
            Message Body
          </Typography>
          <RichTextEditor
            key={templateDialog.open ? `dlg-${templateDialog.eventMessageId}` : "dlg-closed"}
            initialValue={templateDialog.messageBody}
            onChange={(html) => setTemplateDialog((prev) => ({ ...prev, messageBody: html }))}
            minHeight={200}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeTemplateDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveTemplate} disabled={templateSaving}>
            {templateSaving ? <CircularProgress size={18} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---------------- TEMPLATE DELETE CONFIRM ---------------- */}
      <Dialog
        open={templateDeleteConfirm.open}
        onClose={() => setTemplateDeleteConfirm({ open: false, template: null })}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete the template "{templateDeleteConfirm.template?.subject}"?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTemplateDeleteConfirm({ open: false, template: null })}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={handleDeleteTemplate}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

{/* ---------------- EMAIL PREVIEW ---------------- */}
<Dialog
  open={emailPreviewOpen}
  onClose={() => setEmailPreviewOpen(false)}
  fullWidth
  maxWidth="md"
>
  <DialogTitle>
    Email Preview
  </DialogTitle>

  <DialogContent
    dividers
    sx={{
      backgroundColor: "#f4f4f4",
      p: { xs: 1, sm: 3 },
    }}
  >
    {/* EMAIL CONTAINER */}
    <Box
      sx={{
        width: "100%",
        maxWidth: "600px",
        mx: "auto",
        backgroundColor: "#ffffff",
        border: "1px solid #dddddd",
        overflow: "hidden",
      }}
    >

      {/* ================= HEADER ================= */}
      <Box
        sx={{
          backgroundColor: "#333333",
          p: 0,
          textAlign: "center",
        }}
      >
        <img
          src="https://files.constantcontact.com/82886fe2301/302cc6b2-5b9a-4673-8b39-143010d6b262.jpg?rdr=true"
          alt="DirectSubmit"
          style={{
            display: "block",
            width: "100%",
            maxWidth: "600px",
            height: "auto",
            border: 0,
          }}
        />
      </Box>

      {/* ================= BLUE SUBJECT BANNER ================= */}
      <Box
        sx={{
          backgroundColor: "#789eeb",
          px: { xs: 2, sm: 3.75 },
          py: 1.75,
        }}
      >
        <Typography
          component="span"
          sx={{
            color: "#ffffff",
            fontSize: "18px",
            fontWeight: "bold",
            lineHeight: 1.4,
            wordBreak: "break-word",
          }}
        >
          {subject || "(No subject)"}
        </Typography>
      </Box>

      {/* ================= EMAIL BODY ================= */}
      <Box
        sx={{
          backgroundColor: "#ffffff",
          px: { xs: 2, sm: 3.75 },
          py: 3.5,

          fontFamily: "Arial, sans-serif",
          fontSize: "14px",
          color: "#333333",
          lineHeight: 1.6,

          "& p": {
            margin: "0 0 16px",
          },

          "& h1": {
            fontSize: "28px",
            lineHeight: 1.2,
            margin: "16px 0 12px",
          },

          "& h2": {
            fontSize: "22px",
            lineHeight: 1.3,
            margin: "14px 0 10px",
          },

          "& h3": {
            fontSize: "18px",
            lineHeight: 1.4,
            margin: "12px 0 8px",
          },

          "& ul": {
            listStyleType: "disc !important",
            display: "block",
            paddingLeft: "28px",
            margin: "8px 0 12px",
          },

          "& ol": {
            listStyleType: "decimal !important",
            display: "block",
            paddingLeft: "28px",
            margin: "8px 0 12px",
          },

          "& ul li": {
            display: "list-item !important",
            listStyleType: "disc !important",
            marginBottom: "4px",
          },

          "& ol li": {
            display: "list-item !important",
            listStyleType: "decimal !important",
            marginBottom: "4px",
          },

          "& ul ul": {
            listStyleType: "circle !important",
          },

          "& ul ul li": {
            listStyleType: "circle !important",
          },

          "& ol ol": {
            listStyleType: "lower-alpha !important",
          },

          "& ol ol li": {
            listStyleType: "lower-alpha !important",
          },

          "& a": {
            color: "#1976d2",
            textDecoration: "underline",
          },

          "& img": {
  maxWidth: "100%",
  height: "auto",
  display: "block",
  margin: "12px 0",
},

          "& blockquote": {
            borderLeft: "4px solid #ccc",
            margin: "12px 0",
            paddingLeft: "16px",
            color: "#666666",
          },

          "& hr": {
            border: "none",
            borderTop: "1px solid #dddddd",
          },
        }}
      >

        {/* Message */}
        {messageBody ? (
          <div
            dangerouslySetInnerHTML={{
              __html: messageBody,
            }}
          />
        ) : (
          <Typography color="text.secondary">
            No email content.
          </Typography>
        )}

        {/* ================= LOGIN BUTTON ================= */}
        <Box sx={{ margin: "24px 0" }}>
          <a
            href="https://directsubmit.nycastings.com/login"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              backgroundColor: "#789eeb",
              color: "#ffffff",
              padding: "12px 28px",
              textDecoration: "none",
              borderRadius: "4px",
              fontSize: "14px",
              fontWeight: "bold",
              display: "inline-block",
            }}
          >
            Login to Your Account
          </a>
        </Box>

        {/* ================= THANK YOU ================= */}
        <p style={{ margin: 0 }}>
          Thank you,
          <br />
          The DirectSubmit Team
        </p>
      </Box>

      {/* ================= DIVIDER ================= */}
      <Box
        sx={{
          px: { xs: 2, sm: 3.75 },
          backgroundColor: "#ffffff",
        }}
      >
        <Box
          component="hr"
          sx={{
            border: "none",
            borderTop: "1px solid #dddddd",
            margin: 0,
          }}
        />
      </Box>

      {/* ================= FOOTER ================= */}
      <Box
        sx={{
          backgroundColor: "#ffffff",
          px: 2.5,
          py: 1.75,
          textAlign: "center",
          fontFamily: "Arial, sans-serif",
          fontSize: "12px",
          color: "#888888",
          lineHeight: 1.6,
        }}
      >
        <Typography
          component="p"
          sx={{
            margin: "0 0 4px",
            fontSize: "12px",
            color: "#888888",
            lineHeight: 1.6,
          }}
        >
          By using the DirectSubmit platform, you agree to the terms and
          conditions of our{" "}
          <a
            href="https://directsubmit.nycastings.com/terms-and-condition"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: "#4d90fe",
              textDecoration: "underline",
            }}
          >
            Terms of Service
          </a>{" "}
          and{" "}
          <a
            href="https://directsubmit.nycastings.com/privacy-policy"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: "#4d90fe",
              textDecoration: "underline",
            }}
          >
            Privacy Policy
          </a>
          . &copy; DirectSubmit
        </Typography>

        {/* Footer dark bar */}
        <Box
          sx={{
            margin: 0,
            textAlign: "center",
            backgroundColor: "#333333",
            p: 1.25,
            fontSize: "13px",
            color: "#ffffff",
          }}
        >
          <a
            href="https://directsubmit.nycastings.com"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: "#4d90fe",
              textDecoration: "none",
            }}
          >
            DirectSubmit.com
          </a>

          &nbsp; | &nbsp; 1480 Vine St. &nbsp; | &nbsp; Los Angeles, CA 90028
        </Box>
      </Box>
    </Box>
  </DialogContent>

  <DialogActions>
    <Button onClick={() => setEmailPreviewOpen(false)}>
      Close
    </Button>
  </DialogActions>
</Dialog>

      {/* ---------------- SEND CONFIRM ---------------- */}
      <Dialog open={sendConfirmOpen} onClose={() => !sending && setSendConfirmOpen(false)}>
        <DialogTitle>Confirm Send</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Send "<strong>{subject}</strong>" to <strong>{audienceCount ?? "all"}</strong>{" "}
            {selectedAudienceName} recipient(s)? This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSendConfirmOpen(false)} disabled={sending}>
            Cancel
          </Button>
          <Button variant="contained" color="primary" onClick={handleSendMail} disabled={sending}>
            {sending ? <CircularProgress size={18} /> : "Send"}
          </Button>
        </DialogActions>
      </Dialog>

      <Footer />
    </>
  );
};

export default Broadcast;